import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Badge } from '../ui';
import Drawer from './Drawer';
import ImageLightbox from './ImageLightbox';
import {
  previewImage,
  previewImageUpload,
  searchImageReferences,
  commitImage,
  rejectImage,
  imageCandidateUrl,
  imageCandidateReferenceUrl,
} from '../../services/pimApi';

/*
 * AI product image generation + review modal.
 *
 * Two entry modes:
 *   - Generate: pass `sku` only — runs a cheap SerpAPI reference search first
 *     and shows a picker so the user chooses the reference BEFORE the (slow,
 *     costly) Gemini redraw. The user can also upload their own reference.
 *   - Review: pass an existing `candidate` (from the review queue) — skips
 *     generation and goes straight to the side-by-side review.
 *
 * Approving commits the candidate to the chosen destination — either the SKU's
 * primary image (replacing the current one) or appended to the image gallery —
 * tagged ai-generated. Regenerating supersedes (rejects) the previous candidate.
 */

const resolutionLabel = (c) => (c?.width && c?.height ? `${c.width}×${c.height}` : null);

export default function ImageGenModal({ sku, candidate: initialCandidate, currentImageUrl, onClose, onCommitted }) {
  const [phase, setPhase] = useState(initialCandidate ? 'review' : 'searching');
  const [candidate, setCandidate] = useState(initialCandidate || null);
  const [searchCandidates, setSearchCandidates] = useState([]);
  const [selectedRef, setSelectedRef] = useState(null); // original URL chosen in the picker
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [viewing, setViewing] = useState(null); // {src, caption} for the lightbox
  const timerRef = useRef(null);
  const startedRef = useRef(false);
  const fileInputRef = useRef(null);
  // The pending candidate an upload/regenerate should supersede.
  const supersedeRef = useRef(null);

  const startTimer = () => {
    setElapsed(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };
  const stopTimer = () => clearInterval(timerRef.current);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const runSearch = useCallback(async () => {
    setPhase('searching');
    setError(null);
    try {
      const result = await searchImageReferences(sku);
      const found = result.search_candidates || [];
      setSearchCandidates(found);
      if (!found.length) {
        setError('No reference images found on the internet for this SKU. You can upload your own reference below.');
        setPhase('picker');
        return;
      }
      setSelectedRef(found[0].original);
      setPhase('picker');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Reference search failed');
      setPhase('picker');
    }
  }, [sku]);

  const generate = useCallback(
    async (referenceUrl, supersededId) => {
      setPhase('generating');
      setError(null);
      startTimer();
      try {
        const result = await previewImage(sku, referenceUrl);
        // Superseded pending candidate gets rejected so it never lingers in the queue.
        if (supersededId) {
          rejectImage(supersededId).catch(() => {});
        }
        setCandidate(result.candidate);
        if (result.search_candidates?.length) setSearchCandidates(result.search_candidates);
        setPhase('review');
      } catch (e) {
        setError(e?.response?.data?.detail || 'Image generation failed');
        setPhase('error');
      } finally {
        stopTimer();
      }
    },
    [sku]
  );

  const generateFromUpload = useCallback(
    async (file, supersededId) => {
      setPhase('generating');
      setError(null);
      startTimer();
      try {
        const result = await previewImageUpload(sku, file);
        if (supersededId) {
          rejectImage(supersededId).catch(() => {});
        }
        setCandidate(result.candidate);
        setPhase('review');
      } catch (e) {
        setError(e?.response?.data?.detail || 'Image generation from upload failed');
        setPhase('error');
      } finally {
        stopTimer();
      }
    },
    [sku]
  );

  useEffect(() => {
    if (!initialCandidate && !startedRef.current) {
      startedRef.current = true;
      runSearch();
    }
  }, [initialCandidate, runSearch]);

  const openUploadDialog = (supersededId) => {
    supersedeRef.current = supersededId || null;
    fileInputRef.current?.click();
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) generateFromUpload(file, supersedeRef.current);
  };

  const approve = async (destination = 'primary_image') => {
    if (!candidate) return;
    setBusy(true);
    setError(null);
    try {
      const result = await commitImage(candidate.id, destination);
      onCommitted?.({ ...result, destination });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to approve image');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!candidate) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await rejectImage(candidate.id);
      onCommitted?.({ rejected: true, candidate_id: candidate.id });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to reject candidate');
      setBusy(false);
    }
  };

  const referenceUrlOf = (c) => c?.reference_url || null;

  return (
    <Drawer
      title="AI product image"
      sub={`${sku} — reference photo redrawn on the blue studio background`}
      onClose={onClose}
      footer={
        phase === 'picker' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => openUploadDialog(null)}>
              Upload reference…
            </Button>
            <Button onClick={() => generate(selectedRef)} disabled={!selectedRef}>
              Generate from selected
            </Button>
          </>
        ) : phase === 'review' && candidate ? (
          <>
            <Button variant="secondary" onClick={reject} disabled={busy}>
              Reject
            </Button>
            <Button
              variant="secondary"
              onClick={() => generate(referenceUrlOf(candidate), candidate?.id)}
              disabled={busy}
            >
              Regenerate
            </Button>
            <Button variant="secondary" onClick={() => approve('gallery')} disabled={busy}>
              {busy ? 'Saving…' : 'Add to gallery'}
            </Button>
            <Button onClick={() => approve('primary_image')} disabled={busy}>
              {busy ? 'Saving…' : 'Set as primary image'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onFilePicked}
      />

      {phase === 'searching' && (
        <div className="pim-imgen-loading">
          <div className="pim-imgen-spinner" />
          <p>Searching the internet for reference photos of this product…</p>
        </div>
      )}

      {phase === 'picker' && (
        <div className="pim-imgen-picker">
          {error && <div className="pim-error" style={{ marginBottom: 8 }}>{error}</div>}
          {searchCandidates.length > 0 && (
            <>
              <p className="pim-muted" style={{ margin: '0 0 8px' }}>
                Pick the reference photo to redraw. Higher-resolution references give sharper
                results and more legible label text.
              </p>
              <div className="pim-imgen-pick-grid">
                {searchCandidates.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`pim-imgen-pick ${c.original === selectedRef ? 'active' : ''}`}
                    title={`${c.source || ''} — ${c.title || ''}`}
                    onClick={() => setSelectedRef(c.original)}
                  >
                    <img src={c.thumbnail || c.original} alt={c.title || 'reference option'} loading="lazy" />
                    <span className="pim-imgen-pick-meta">
                      <span className="pim-imgen-pick-source">{c.source || ''}</span>
                      {resolutionLabel(c) && (
                        <span className="pim-imgen-pick-res">{resolutionLabel(c)}</span>
                      )}
                    </span>
                    {i === 0 && <Badge variant="info">recommended</Badge>}
                  </button>
                ))}
              </div>
            </>
          )}
          <p className="pim-muted" style={{ marginTop: 12 }}>
            Or upload your own reference photo (e.g. an official marketing shot) with the
            “Upload reference…” button below.
          </p>
        </div>
      )}

      {phase === 'generating' && (
        <div className="pim-imgen-loading">
          <div className="pim-imgen-spinner" />
          <p>Redrawing the product on the blue studio background at 2K…</p>
          <p className="pim-muted">This usually takes 20–60 seconds. Elapsed: {elapsed}s</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="pim-imgen-loading">
          <p className="pim-error">{error}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={runSearch}>
              Back to references
            </Button>
            <Button variant="secondary" onClick={() => openUploadDialog(null)}>
              Upload reference…
            </Button>
          </div>
        </div>
      )}

      {phase === 'review' && candidate && (
        <>
          {error && <div className="pim-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div className="pim-imgen-compare">
            <div className="pim-imgen-pane">
              <div className="pim-imgen-pane-head">
                <strong>Reference</strong>
                {candidate.reference_source && <Badge variant="neutral">{candidate.reference_source}</Badge>}
              </div>
              <img
                src={imageCandidateReferenceUrl(candidate.id)}
                alt="Reference"
                className="pim-media-clickable"
                title="Click to view full size"
                onClick={() =>
                  setViewing({ src: imageCandidateReferenceUrl(candidate.id), caption: 'Reference image' })
                }
              />
              {candidate.reference_url && (
                <a href={candidate.reference_url} target="_blank" rel="noreferrer" className="pim-muted">
                  Source URL
                </a>
              )}
            </div>
            <div className="pim-imgen-pane">
              <div className="pim-imgen-pane-head">
                <strong>Generated</strong>
                <Badge variant="info">AI</Badge>
              </div>
              <img
                src={imageCandidateUrl(candidate.id)}
                alt="Generated candidate"
                className="pim-media-clickable"
                title="Click to view full size"
                onClick={() =>
                  setViewing({ src: imageCandidateUrl(candidate.id), caption: `Generated — ${sku}` })
                }
              />
              <span className="pim-muted">
                {candidate.width && candidate.height ? `${candidate.width}×${candidate.height}px` : ''}
              </span>
            </div>
            {currentImageUrl && (
              <div className="pim-imgen-pane">
                <div className="pim-imgen-pane-head">
                  <strong>Current image</strong>
                  <Badge variant="warning">replaced if set as primary</Badge>
                </div>
                <img
                  src={currentImageUrl}
                  alt="Current primary"
                  className="pim-media-clickable"
                  title="Click to view full size"
                  onClick={() => setViewing({ src: currentImageUrl, caption: 'Current primary image' })}
                />
              </div>
            )}
          </div>

          <div className="pim-imgen-refs">
            {searchCandidates.length > 1 && (
              <>
                <p className="pim-muted" style={{ margin: '12px 0 6px' }}>
                  Not the right reference? Pick another photo found for this SKU:
                </p>
                <div className="pim-imgen-ref-strip">
                  {searchCandidates.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`pim-imgen-ref ${c.original === candidate.reference_url ? 'active' : ''}`}
                      title={`${c.source || ''} — ${c.title || ''}`}
                      disabled={busy}
                      onClick={() => generate(c.original, candidate?.id)}
                    >
                      <img src={c.thumbnail || c.original} alt={c.title || 'reference option'} loading="lazy" />
                      <span>{c.source || ''}</span>
                      {resolutionLabel(c) && <span className="pim-imgen-ref-res">{resolutionLabel(c)}</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
            <Button
              variant="secondary"
              disabled={busy}
              style={{ marginTop: 8 }}
              onClick={() => openUploadDialog(candidate?.id)}
            >
              Upload reference…
            </Button>
          </div>
        </>
      )}

      {viewing && (
        <ImageLightbox src={viewing.src} caption={viewing.caption} onClose={() => setViewing(null)} />
      )}
    </Drawer>
  );
}
